import React, { useState, useCallback } from 'react';
import Code from '../../Code/Code';
import EmbedCodeCopyButton from '../../EmbedCodeCopyButton/EmbedCodeCopyButton';
import ComboBox from '../../../../components/ComboBox/ComboBox';
import { UnControlled as ReactCodeMirror } from 'react-codemirror2';
import getHostUrlForIntegrations from '../../../../../services/mini/getHostUrlForIntegrations';

const jsCodeMirrorOptions = {
    lineNumbers: false,
    readOnly: true,
    mode: {
        name: 'javascript',
    },
    scrollbarStyle: null,
    cursorScrollMargin: -1000,
};

const htmlCodeMirrorOptions = {
    lineNumbers: false,
    readOnly: true,
    mode: {
        name: 'htmlmixed',
    },
    scrollbarStyle: null,
    cursorScrollMargin: -1000,
};
const googleFontCode = '<link href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap" rel="stylesheet">';

const alanIonicReactInstallCode = 'npm install @alan-ai/cordova-plugin-alan-voice –save';

const alanIonicReactInstallButtonCode = 'npm install @alan-ai/alan-button –save';

const alanIonicReactDefinitionCode = `declare namespace JSX {
  interface IntrinsicElements {
    [tagName:string]: any
  }
}`;

const alanIonicAngularInstallCode = 'npm install @alan-ai/cordova-plugin-alan-voice –save';

const alanIonicAngularInstallButtonCode = 'npm install @alan-ai/alan-button –save';

const alanIonicAngularCustomHtmlSchemaCode = `import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
@NgModule({
...
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
...
})`;

const alanIonicAngularRegisterCode = `import { defineCustomElements as alanBtnDefineCustomElements } from '@alan-ai/alan-button/dist/loader';
alanBtnDefineCustomElements(window);`;

function alanIonicAngularButtonTabCode(alanKey) {
    return `<--  app.component.html -->
<ion-app>
    <ion-router-outlet></ion-router-outlet>
    <alan-button #alanBtnEl alan-key="${alanKey}" alan-host="${getHostUrlForIntegrations()}"></alan-button>
</ion-app>`;
}

const alanIonicAngularButtonImportCode = `import "@alan-ai/alan-button";
import { Component, ElementRef, ViewChild } from '@angular/core';
@ViewChild('alanBtnEl', {static:false}) alanBtnComponent: ElementRef<HTMLAlanButtonElement>;`;

const alanIonicAngularHandleCommandsCode = `this.alanBtnComponent.nativeElement.addEventListener('command', (data) => {
\tconst commandData = (<CustomEvent>data).detail;

\tif (commandData.command === 'navigation') {
\t\t//call client code that will react to the received command
\t}
});`;

function alanIonicAngularComponentCode(alanKey) {
    return `alanBtn({ 
  key: '${alanKey}',
  host: '${getHostUrlForIntegrations()}',
  onCommand: (commandData) => {
    if (commandData.command === 'go:back') {
      //call client code that will react to the received command
    }
  },
});`;
}

const codeModes = [
    { name: 'Angular', key: 'angular' },
    { name: 'React', key: 'react' },
];

export function IonicTab({ alanKey }) {
    const [codeMode, setCodeMode] = useState('angular');

    const changeCodeModeCb = useCallback((codeModeObj) => {
        setCodeMode(codeModeObj.key);
    }, []);

    return <div className="embed-code-tab">
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description">
            </div>
            <div className="embed-code-tab_dash"></div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_not-highlighted-code">
                    <br />
                </div>
            </div>
        </div>
        <ComboBox values={codeModes}
                  valueKey="key"
                  valueName="name"
                  classes="code-mode-combo-box light-blue-with-border"
                  selectedValueId={codeMode}
                  onValueSelect={changeCodeModeCb}
                  renderItem={(codeModeItem) => {
                      return <div style={{ paddingLeft: '8px' }}
                                  key={codeModeItem.key}>
                          {codeModeItem.name}
                      </div>;
                  }} />
        { codeMode === 'react' &&
        <>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Add the Alan Cordova plugin
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <Code highlighted={true} category="bash" type="code">
                            {alanIonicAngularInstallCode}
                        </Code>
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicReactInstallCode} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Add the Alan button package
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <ReactCodeMirror
                            value={alanIonicReactInstallButtonCode}
                            options={jsCodeMirrorOptions} />
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicReactInstallButtonCode} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Create definition file
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <ReactCodeMirror
                            value={alanIonicReactDefinitionCode}
                            options={jsCodeMirrorOptions} />
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicReactDefinitionCode} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Create definition file
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <ReactCodeMirror
                            value={alanIonicReactDefinitionCode}
                            options={jsCodeMirrorOptions} />
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicReactDefinitionCode} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Add the Alan button to your component. Handle commands from the Alan AI Studio
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <ReactCodeMirror
                            value={alanIonicAngularComponentCode(alanKey)}
                            options={jsCodeMirrorOptions} />
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicAngularComponentCode(alanKey)} />
                        </div>
                    </div>
                </div>
            </div>
        </>}


        { codeMode === 'angular' &&
        <>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Add the Alan Cordova plugin
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <Code highlighted={true} category="bash" type="code">
                            {alanIonicAngularInstallCode}
                        </Code>
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicAngularInstallCode} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Add the Alan button package
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <ReactCodeMirror
                            value={alanIonicAngularInstallButtonCode}
                            options={jsCodeMirrorOptions} />
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicAngularInstallButtonCode} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Enable custom HTML tags
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <ReactCodeMirror
                            value={alanIonicAngularCustomHtmlSchemaCode}
                            options={jsCodeMirrorOptions} />
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicAngularCustomHtmlSchemaCode} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Register the Alan button component
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <ReactCodeMirror
                            value={alanIonicAngularRegisterCode}
                            options={jsCodeMirrorOptions} />
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicAngularRegisterCode} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Add the Alan button HTML tag
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <ReactCodeMirror
                            value={alanIonicAngularButtonTabCode(alanKey)}
                            options={htmlCodeMirrorOptions} />
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicAngularButtonTabCode(alanKey)} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Import the Alan button
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <ReactCodeMirror
                            value={alanIonicAngularHandleCommandsCode}
                            options={jsCodeMirrorOptions} />
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicAngularButtonImportCode} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc5">
                    Handle commands from the Alan AI Studio
                </div>
                <div className="embed-code-tab_dash embed-code-desc5">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <ReactCodeMirror
                            value={alanIonicAngularHandleCommandsCode}
                            options={jsCodeMirrorOptions} />
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={alanIonicAngularHandleCommandsCode} />
                        </div>
                    </div>
                </div>
            </div>
        </> }
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc2">
                Add a link to the font
            </div>
            <div className="embed-code-tab_dash embed-code-desc2">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted={true} category="web" type="tag">
                        {googleFontCode}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={googleFontCode} />
                    </div>
                </div>
            </div>
        </div>
    </div>;
}
